import React, { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import Table, {
  THead,
  THeadRow,
  THeadCell,
  TBody,
  TRow,
  TCell,
} from '../Table';
import styles from './BrandsTable.module.css';
import {
  ANALYTICS,
  CHANNELS,
  ElementTypes,
  SortOrder,
} from '../../../constants/common';
import BrandsInfoTableCell from './BrandsInfoTableCell';
import NoDataInline, { NoDataInlineVariant } from '../../atoms/NoDataInline';
import { TooltipPosition } from '../InfoTooltip';
import { ActionNames, Citation } from '../../../types/Content';
import {
  getEncodedUserNtid,
  sortObjectArray,
} from '../../../store/markettingContentGeneration/helpers/common';
import ContentActions from '../../atoms/ContentActions/ContentActions';
import CopyIcon from '../../atoms/ContentOptionIcons/CopyIcon';
import { translate } from '../../../localization';
import { copyAsTable } from '../../../utils/copyToClipboard';
import { useSnackBar } from '../../../hooks/snackBar/useSnackBar';
import SnackBar, {
  SnackBarPosition,
  SnackBarVariant,
} from '../../atoms/SnackBar';
import Logger from '../../../utils/logger';
import { trackQuickCopyContent } from '../../../services/Analytics/copyContent';
import { contentParamsSegment } from '../../../store/markettingContentGeneration/stateSegments/paramsSegment';
import {
  selectedChannel,
  selectedDestinationFormat,
} from '../../../store/markettingContentGeneration/stateSegments/contentListSegment';

export type ColumnConfig = {
  title: string;
  sortable: boolean;
  showInfoIcon: boolean;
  infoText?: string;
  tooltipPosition?: TooltipPosition;
  value: string;
  renderer?: (data: TableDataConfig) => React.ReactNode;
  columnHeaderStyle?: React.CSSProperties;
  columnContentStyle?: React.CSSProperties;
  customSort?: boolean;
  sort?: (order: string, key: string) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TableDataConfig = any;

type BrandsInfoTableProps = {
  columnsConfiguration: ColumnConfig[];
  tableData: TableDataConfig[];
  gridColumnsStyle: string;
  className?: string;
  showCopyForRows?: boolean;
};

type SortOrderType = {
  [key: string]: SortOrder;
};

const textActions = [
  {
    name: ActionNames.COPY_TEXT,
    label: translate('common_copy_text'),
    enabled: true,
    icon: <CopyIcon />,
  },
];

const BrandsInfoTable: React.FC<BrandsInfoTableProps> = ({
  columnsConfiguration,
  tableData,
  gridColumnsStyle,
  className = '',
  showCopyForRows = false,
}) => {
  const {
    titleText,
    showSnackBar,
    isSnackBarVisible,
    hideSnackBar,
    notificationText,
    snackBarTheme,
  } = useSnackBar(10000);
  const channel = useAtomValue(selectedChannel);
  const destFormat = useAtomValue(selectedDestinationFormat);
  const paramsSegment = useAtomValue(contentParamsSegment);
  const brandName = paramsSegment?.currentSelectedBrand || '';
  const targetAudience = paramsSegment?.currentSelectedAudience || '';
  const [rowIndex, setRowIndex] = useState(0);
  const [actionsPosition, setActionsPosition] = useState({ top: 0, left: 0 });
  const headerRowRef = useRef<HTMLDivElement>(null);
  const refs = useRef<{ [key: number]: React.RefObject<HTMLDivElement> }>({});
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Helper function to create or get a ref for a specific row
  const getRowRef = (id: number): React.RefObject<HTMLDivElement> => {
    if (!refs.current[id]) {
      refs.current[id] = React.createRef<HTMLDivElement>();
    }
    return refs.current[id];
  };

  const actionsRef = useRef<HTMLDivElement>(null);
  const [showTextActions, setShowTextActions] = useState(false);
  const [isHoveringActions, setIsHoveringActions] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getRowValue = (rowData: any, key: string): string => {
    return rowData[key];
  };

  const [rowsData, setRowsData] = useState(tableData);
  const [sortOrder, setSortOrder] = useState<SortOrderType>({});

  useEffect(() => {
    setRowsData(tableData);
  }, [tableData?.length]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const ConditionalComponent = (
    rowValue: string | number | Citation[],
    column: ColumnConfig
  ) => {
    let content;

    // Check if number is 0 or null and show NoDataInline
    if (typeof rowValue === 'number' && (rowValue === 0 || rowValue === null)) {
      content = (
        <div className={styles['brands-table__cell-no-data']}>
          <NoDataInline variant={NoDataInlineVariant.SMALL_TABLE_FIELD} />
        </div>
      );
    } else if (typeof rowValue === 'string' && rowValue.length === 0) {
      if (column.value === 'rank') {
        content = (
          <div className={styles['brands-table__cell-no-data']}>
            <NoDataInline variant={NoDataInlineVariant.SMALL_TABLE_FIELD} />
          </div>
        );
      } else {
        content = (
          <div className={styles['brands-table__cell-no-data']}>
            <NoDataInline variant={NoDataInlineVariant.MEDIUM_TABLE_FIELD} />
          </div>
        );
      }
    } else if (typeof rowValue === 'undefined') {
      if (column.value === 'rank') {
        content = (
          <div className={styles['brands-table__cell-no-data']}>
            <NoDataInline variant={NoDataInlineVariant.SMALL_TABLE_FIELD} />
          </div>
        );
      } else {
        content = (
          <div className={styles['brands-table__cell-no-data']}>
            <NoDataInline variant={NoDataInlineVariant.MEDIUM_TABLE_FIELD} />
          </div>
        );
      }
    } else if (typeof rowValue === 'string' || Array.isArray(rowValue)) {
      content = (
        <BrandsInfoTableCell
          key={column.value}
          rowValue={rowValue}
          columnName={column.value}
        />
      );
    } else if (typeof rowValue === 'number') {
      // Convert number to string for BrandsInfoTableCell
      content = (
        <BrandsInfoTableCell
          key={column.value}
          rowValue={String(rowValue)}
          columnName={column.value}
        />
      );
    } else {
      // For JSX content like claim descriptions with links, use BrandsInfoTableCell
      content = (
        <BrandsInfoTableCell
          key={column.value}
          rowValue={rowValue}
          columnName={column.value}
        />
      );
    }

    return column.renderer
      ? column.renderer(rowValue)
      : (content as string | JSX.Element);
  };
  const handleMouseOver = (rowId: number): void => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    setShowTextActions(true);
    setRowIndex(rowId);

    // Calculate position for ContentActions
    const rowRef = refs.current[rowId];
    const tableContainer = tableContainerRef.current;

    if (rowRef?.current && tableContainer) {
      const rowRect = rowRef.current.getBoundingClientRect();
      const containerRect = tableContainer.getBoundingClientRect();

      setActionsPosition({
        top: rowRect.top - containerRect.top, // Top align with the row
        left: containerRect.width + 10, // Position outside the container
      });
    }
  };

  const handleMouseOut = (): void => {
    // Only set timeout if not hovering over actions
    if (!isHoveringActions) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowTextActions(false);
      }, 25); // Reduced delay for better responsiveness
    }
  };

  const handleActionsMouseEnter = (): void => {
    // Clear the timeout when mouse enters the actions
    setIsHoveringActions(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleActionsMouseLeave = (): void => {
    // Hide immediately when leaving the actions
    setIsHoveringActions(false);
    setShowTextActions(false);
  };
  const onCopyClicked = (successful: boolean) => {
    if (successful) {
      showSnackBar(translate('snackbar_copied_message'), SnackBarVariant.INFO);
      const channelName =
        channel?.toLowerCase() === CHANNELS.UPLOAD.toLowerCase() &&
        destFormat.length
          ? destFormat?.toLowerCase()
          : channel?.toLowerCase();
      trackQuickCopyContent(
        ANALYTICS.QUICK_COPY_ELEMENT,
        brandName,
        channelName,
        targetAudience,
        ElementTypes.CLAIM,
        getEncodedUserNtid()
      );
    } else {
      Logger.error(new Error('Unable to copy text to clipboard:'));
      showSnackBar(
        translate('snackbar_copied_message_failed'),
        SnackBarVariant.ERROR
      );
    }
  };
  const handleActionClick = (actionName: string, id?: number | null): void => {
    const rowRefValue = refs.current[id as number];
    if (actionName === ActionNames.COPY_TEXT) {
      if (
        rowRefValue &&
        rowRefValue.current &&
        headerRowRef &&
        headerRowRef.current
      ) {
        copyAsTable(rowRefValue.current, headerRowRef.current, onCopyClicked);
      }
    }
  };

  return (
    <>
      <div ref={tableContainerRef} style={{ position: 'relative' }}>
        <Table className={`${styles['brands-table']} ${className}`}>
          <THead>
            <THeadRow
              className={`${styles['brands-table__row__grid']} ${styles['brands-table__head__row']}`}
              style={{
                gridTemplateColumns: gridColumnsStyle,
              }}
              rowRef={headerRowRef}
            >
              {columnsConfiguration.map(column => {
                return (
                  <THeadCell
                    key={`${column.title}`}
                    name={column.title}
                    isSortable={column.sortable}
                    onClick={e => {
                      const targetSVG = (e.target as HTMLElement).closest(
                        'svg'
                      );
                      const direction = targetSVG?.getAttribute('data-order');
                      const currentSortOrder =
                        direction === 'desc'
                          ? SortOrder.DESCENDING
                          : SortOrder.ASCENDING;

                      setSortOrder(prev => ({
                        ...prev,
                        [column.value]: currentSortOrder,
                      }));
                      if (column.customSort && column.sort && direction) {
                        column.sort(direction, column.value);
                      } else {
                        const sortedData = sortObjectArray(
                          tableData,
                          column.value,
                          currentSortOrder
                        );

                        setRowsData(sortedData);
                      }
                    }}
                    sortOrder={sortOrder[column.value]}
                    isSorted={!!sortOrder[column.value]}
                    showInfoIcon={column.showInfoIcon}
                    infoText={column.infoText}
                    tooltipPosition={column.tooltipPosition}
                    className={styles['brands-table__head_cell']}
                    style={column.columnHeaderStyle || {}}
                  >
                    {column.title}
                  </THeadCell>
                );
              })}
            </THeadRow>
          </THead>
          {rowsData && rowsData?.length > 0 ? (
            <TBody
              className={styles['brands-table__body']}
              onScroll={() => undefined}
            >
              {rowsData?.map((rowData, index: number) => {
                return (
                  <TRow
                    key={rowData.id}
                    className={`${styles['brands-table__row__grid']} ${
                      styles['brands-table__row']
                    } ${showCopyForRows && styles['brands-table__row_hover']} ${
                      showCopyForRows &&
                      showTextActions &&
                      rowIndex === index &&
                      styles['brands-table__row_active']
                    }`}
                    style={{
                      gridTemplateColumns: gridColumnsStyle,
                      position: 'relative',
                    }}
                    rowRef={getRowRef(index)}
                    onMouseOver={() => handleMouseOver(index)}
                    onMouseOut={handleMouseOut}
                  >
                    {columnsConfiguration.map(column => {
                      const rowValue = getRowValue(rowData, column.value);

                      return (
                        <TCell
                          key={column.title}
                          aria-label={column.title}
                          className={`${styles['brands-table__cell']} ${styles['brands-table__cell-ellipsis']}`}
                          style={column.columnContentStyle || {}}
                        >
                          {ConditionalComponent(rowValue, column)}
                        </TCell>
                      );
                    })}
                  </TRow>
                );
              })}
            </TBody>
          ) : (
            <NoDataInline variant={NoDataInlineVariant.NO_TABLE_DATA} />
          )}{' '}
        </Table>
        {showCopyForRows && showTextActions && textActions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: `${actionsPosition.top}px`,
              left: `${actionsPosition.left}px`,
              zIndex: 1001,
            }}
            onMouseEnter={handleActionsMouseEnter}
            onMouseLeave={handleActionsMouseLeave}
          >
            <ContentActions
              actions={textActions}
              handleActionClick={handleActionClick}
              actionsRef={actionsRef}
              classes={styles.row_actions}
              rowIndex={rowIndex}
            />
          </div>
        )}
      </div>
      {isSnackBarVisible && (
        <SnackBar
          theme={snackBarTheme}
          titleText={titleText}
          notificationText={notificationText}
          hideSnackBar={hideSnackBar}
          position={SnackBarPosition.BOTTOM_CENTER}
        />
      )}
    </>
  );
};

BrandsInfoTable.displayName = 'BrandsInfoTable';

export default React.memo(BrandsInfoTable);
